import React, { useEffect, useRef, useState } from "react";
import { useGuidedTour } from "./GuidedTourContext";
import { createPortal } from "react-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ProgressDots from "./ProgressDots";
import { computePosition, offset, flip, shift, arrow } from "@floating-ui/dom";


import { useDispatch,useSelector  } from "react-redux";
import { setAnchorPosition } from "../../../Map/store/MapDisplaySlice.js";
import { setOpenModal, setMenuIsOpen } from "../../../ControlPanel/store/controlPanelSlice.js";
import { getRoomsWithReception } from "../../../Map/store/MapDisplayThunks.js";

const ANIMATION_STYLE_ID = "guided-tour-animations";

function injectAnimationStyles() {
  if (document.getElementById(ANIMATION_STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = ANIMATION_STYLE_ID;
  style.textContent = `
    @keyframes guided-fade-in {
      from { opacity: 0; transform: scale(0.94) translateY(4px); }
      to   { opacity: 1; transform: scale(1)    translateY(0);   }
    }
    @keyframes guided-fade-in-centered {
      from { opacity: 0; transform: translate(-50%, -48%) scale(0.94); }
      to   { opacity: 1; transform: translate(-50%, -50%) scale(1);    }
    }
    .guided-btn {
      transition: background 0.15s ease, color 0.15s ease,
                  border-color 0.15s ease, transform 0.1s ease;
    }
    .guided-btn:hover  { filter: brightness(0.94); }
    .guided-btn:active { transform: scale(0.97); }
    .guided-btn-primary:hover { filter: brightness(1.08); }
    .guided-arrow-animated {
      transition: left 0.22s ease, top 0.22s ease;
    }
  `;
  document.head.appendChild(style);
}

//─── Highlight de l'élément cible via box-shadow ──────────────────────────────
let previousHighlightedEl = null;
 
function highlightElement(el) {
  if (previousHighlightedEl && previousHighlightedEl !== el) {
    previousHighlightedEl.style.boxShadow = "";
    previousHighlightedEl.style.zIndex = "";
    previousHighlightedEl.style.position = "";
  }
  if (!el) return;
  el.style.transition = "box-shadow 0.25s ease, border-radius 0.25s ease, transform 0.25s ease";
  el.style.boxShadow = "0 0 0 4px rgba(71,154,50,0.35), 0 0 0 9999px rgba(0,0,0,0.55)";
  el.style.position = "relative";
  el.style.zIndex = "9999998";
  previousHighlightedEl = el;
}
 
function clearHighlight() {
  if (previousHighlightedEl) {
    previousHighlightedEl.style.boxShadow = "";
    previousHighlightedEl.style.zIndex = "";
    // on laisse la position pour éviter un saut de layout
    previousHighlightedEl = null;
  }
}

export default function GuidedTooltip() {
  const {
    isOpen,
    currentStep,
    stepIndex,
    totalSteps,
    visibleStepIndex,
    showProgress,
    next,
    prev,
    close,
    start,
    goToStep,
  } = useGuidedTour();

  
  const { mapConfig } = useSelector((state) => state.MapDisplay);
  const  mapId = mapConfig.mapId


  const step = currentStep;
  const dispatch = useDispatch();
  const floatingRef = useRef(null);
  const arrowRef = useRef(null);
  
  const menuObserverRef = useRef(null);
  const allowForceMenuRef = useRef(true);

  const centeredRef = useRef(false);

  // Auto open menu profile
  let menuObserver = null;

  const rooms = useSelector(
    (state) => state.MapDisplay.roomsData
  );

  const [isReady, setIsReady] = useState(false);
  const [arrowData, setArrowData] = useState({
    x: null,
    y: null,
    side: "bottom",
  });

  const [targetRect, setTargetRect] = useState(null);
  // Responsive 
  const isMobile = window.innerWidth < 700;

  // Injection unique des styles d'animation
  useEffect(() => { injectAnimationStyles(); }, []);
 
  // Nettoyage du highlight à la fermeture
  useEffect(() => {
    if (!isOpen) clearHighlight();
  }, [isOpen]);

  const closeMainMenu = () => {
    allowForceMenuRef.current = false;
  
    menuObserverRef.current?.disconnect();
  
    dispatch(setMenuIsOpen(false));
  
    const menu = document.querySelector(".config-menu");
    const items = document.querySelector(".config-items");
  
    document.body.classList.remove("main-menu-open");
    menu?.classList.remove("config-menu-open");
    items?.classList.remove("open");
  };

  useEffect(() => {
    centeredRef.current = false;
    if (!isOpen || !step) return;

    let cleanupMapSync = null;
    setIsReady(false);
    setTargetRect(null);
    clearHighlight();
  
    // -------------------------------------------------
    
    if (step.beforeOpenMenu) {
      const menu = document.querySelector(".config-menu");
      const items = document.querySelector(".config-items");

      const forceOpenMenu = () => {
        document.body.classList.add("main-menu-open");
        menu?.classList.add("config-menu-open");
        items?.classList.add("open");
      };

      forceOpenMenu();

      if (menu) {
        menuObserverRef.current = new MutationObserver(() => {
          if (
            allowForceMenuRef.current &&
            !menu.classList.contains("config-menu-open")
          ) {
            forceOpenMenu();
          }
        });

        menuObserverRef.current.observe(menu, {
          attributes: true,
          attributeFilter: ["class"],
        });
      }
    }
    
    // -------------------------------------------------
    // Click next
    let clickTarget = null;
    let handleClick = null;

    let clickInterval = null;

    if (step.action === "click-next") {

      handleClick = () => {
        if(step.target === "#tour-btn-room-create"){
          dispatch(setAnchorPosition(null));
          setTimeout(() => next(), 220);
        } else {
          next();
        }
      };

      const bindClick = () => {
        const el = document.querySelector(step.target);

        if (!el) return;

        el.removeEventListener("click", handleClick);
        el.addEventListener("click", handleClick, { once: true });
      };

      bindClick();

      clickInterval = setInterval(bindClick, 400);
    }
    //--------------------------------------------------------
    // Centre la map sur la room ciblée
    if (step.centerMapOnTarget && rooms?.length) {
      const room = rooms.find(
        (r) =>
          r.label?.toLowerCase() === step.roomLabel?.toLowerCase()
      );

      const map = window.leafletMap;

      const el = document.querySelector(step.target);
      if (el && step.action === "click-next") {
        el.addEventListener("click", handleClick, { once: true });
      }

      if (map) {
        map.once("moveend", () => {
          requestAnimationFrame(() => {
            calculate();
          });
        });
      }

      const tryCenter = () => {
        const map = window.leafletMap;
      
        if (!room || !map || centeredRef.current) return false;
      
        centeredRef.current = true;

        map.once("moveend", () => {
          const el = document.querySelector(step.target);
          el?.addEventListener("click", handleClick, { once: true });
        });
      
        map.setView(room.position, 2, {
          animate: true,
          duration: 0.6

        });
      
        return true;
      };
      
      if (!tryCenter()) {
        setTimeout(tryCenter, 300);
      }
    }

    // -------------------------------------------------
    const calculate = () => {
      if (step.finalStep) {
        setIsReady(true);
        return;
      }

      const reference = document.querySelector(step.target);
      const floating = floatingRef.current;
      const arrowEl = arrowRef.current;

      if (!reference || !floating) return;
      const rect = reference.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      setTargetRect(rect);

      if(!step.openMobileMore) highlightElement(reference);
      if (isMobile && reference) {
         const rect = reference.getBoundingClientRect();
      
        const alreadyVisible =
          rect.top >= 0 &&
          rect.bottom <= window.innerHeight;
      
        if (!alreadyVisible) {
          reference.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "nearest",
          });
        }
      }

      computePosition(reference, floating, {
        strategy: "fixed",
        placement:  step.placement || "top",
        middleware: [
          offset(24),
          flip({
            fallbackPlacements: ["top", "bottom", "right", "left"],
            padding: 12,
          }),
          shift({ padding: 12 }),
          arrow({ element: arrowEl }),
        ],
      }).then(({ x, y, placement, middlewareData }) => {
        floating.style.transform = `translate3d(${x}px, ${y}px, 0)`;
        floating.style.left = "0";
        floating.style.top = "0";

        const sideMap = {
          top: "bottom",
          bottom: "top",
          left: "right",
          right: "left",
        };

        setArrowData({
          x: middlewareData.arrow?.x ?? null,
          y: middlewareData.arrow?.y ?? null,
          side: sideMap[placement.split("-")[0]],
        });

        setIsReady(true);
      });
    };

    // -------------------------------------------------
    let rafId = null;
    let attempts = 0;

    if (step.openMobileMore && window.innerWidth < 700) {
      const btn = document.querySelector(".icon-more");
    
      if (btn) {
        btn.click();
      }
    }

    const tryPosition = () => {
      if (step.finalStep) {
        setIsReady(true);
        return;
      }

      const reference = document.querySelector(step.target);
      const floating = floatingRef.current;

      attempts++;

      if (!reference || !floating) {
        if (attempts < 30) {
          rafId = requestAnimationFrame(tryPosition);
        }
        return;
      }

      const rect = reference.getBoundingClientRect();

      if (!rect.width || !rect.height) {
        if (attempts < 30) {
          rafId = requestAnimationFrame(tryPosition);
        }
        return;
      }
      
       // bouton Room Innovation => attendre un peu
       if(step.target === "#tour-btn-room-create"){
        setTimeout(calculate, 400);
        return;
     }
      if (step.centerMapOnTarget) {
        setTimeout(calculate, 350);
        return;
      }
    
      calculate();
    };

    const timer = setTimeout(() => {
      rafId = requestAnimationFrame(tryPosition);
    }, step.beforeOpenMenu ? 250 : 50);

    window.addEventListener("resize", calculate);
    window.addEventListener("scroll", calculate, true);

    // -------------------------------------------------
    return () => {
      clearTimeout(timer);

      if (rafId) cancelAnimationFrame(rafId);

      window.removeEventListener("resize", calculate);
      window.removeEventListener("scroll", calculate, true);

      if (clickTarget && handleClick) {
        clickTarget.removeEventListener("click", handleClick);
      }

      if (menuObserver) {
        menuObserver.disconnect();
      }
      if (cleanupMapSync) {
        cleanupMapSync();
      }
      if (clickInterval) clearInterval(clickInterval);
    };
  }, [isOpen, step, next]);

  useEffect(() => {
    const res = dispatch(getRoomsWithReception ({body :{mapId}})).unwrap();
  },[]);

  if (!isOpen || !step) return null;

  if (!isOpen || !step) return null;
  const isFinal = step.finalStep;

  return createPortal(
    <>
    
      {/* Overlay */}
      {targetRect && !isFinal && !step.openMobileMore && (
        <>
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: targetRect.top,
              background: "rgba(0,0,0,0.55)",
              zIndex: 999999,

              pointerEvents: "none"
            }}
          />

          <div
            style={{
              position: "fixed",
              top: targetRect.bottom,
              left: 0,
              width: "100%",
              bottom: 0,
              background: "rgba(0,0,0,0.55)",
              zIndex: 9999997,
              pointerEvents: "none"

              
            }}
          />

          <div
            style={{
              position: "fixed",
              top: targetRect.top,
              left: 0,
              width: targetRect.left,
              height: targetRect.height,
              background: "rgba(0,0,0,0.55)",
              zIndex: 9999997,

              pointerEvents: "none"

            }}
          />

          <div
            style={{
              position: "fixed",
              top: targetRect.top,
              left: targetRect.right,
              right: 0,
              height: targetRect.height,
              background: "rgba(0,0,0,0.55)",
              zIndex: 9999997,

              pointerEvents: "none"

            }}
          />
        </>
      )}

      {/* Popup */}
      <div
        ref={floatingRef}
        style={{
        position: "fixed",
        top: isFinal ? "50%" : 0,
        left: isFinal ? "50%" : 0,
        transform: isFinal ? "translate(-50%, -50%)" : "none",
        visibility: isReady ? "visible" : "hidden",
        /* Responsive */
        width: isMobile ? "calc(100vw - 1.25rem)" : "17.5rem",
        maxWidth: "min(26.25rem, calc(100vw - 1.25rem))",
        minWidth: isMobile ? "unset" : "17.5rem",
        maxHeight: isMobile ? "80vh" : "70vh",
        overflowY: "auto",
        background: "#fff",
        borderRadius: isMobile ? "0.875rem" : "0.75rem",
        padding: isMobile ? "0.875rem" : "1rem",
        boxShadow: "0 0.625rem 1.875rem rgba(0,0,0,0.18)",
        zIndex: 9999999,
        textAlign: isFinal ? "center" : "left",
        
        transition: "opacity 0.2s ease",
        opacity: isReady ? 1 : 0,

        /* Mobile comfort */
        maxHeight: isMobile ? "85vh" : "unset",
        overflowY: isMobile ? "auto" : "visible",
      }}
    >
        {/* Badge */}
        {!step.passive && showProgress && (
          <div
            style={{
              marginBottom: "0.5rem",
              color: "#479A32",
              fontSize: "0.8125rem",
              background: "#DEFCD9",
              display: "inline-block",
              padding: "1.875rem 0.5rem",
              borderRadius: "0.5rem",
              fontWeight: "500",
            }}
          >
            <span
              style={{
                width: "0.5rem",
                height: "0.5rem",
                borderRadius: "50%",
                background: "#479A32",
                display: "inline-block",
                marginRight: "0.375rem",
              }}
            />
            Étape {visibleStepIndex}/{totalSteps}
          </div>
        )}

        {/* Icon */}
        <div
          style={{
            width: "2rem",
            height: "2rem",
            backgroundColor: isFinal ? "#DEFCD9" : "#479A32",
            borderRadius: isFinal ? "50%" : "0.375rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "0.75rem", marginLeft: step.finalStep ? "auto" : 0, marginRight: step.finalStep ? "auto" : 0,
          }}
        >
          <span
            className="material-symbols-outlined"
            style={{
              fontSize: "1.125rem",
              color: isFinal ? "#479A32" : "#fff",
            }}
          >
            {step.icon}
          </span>
        </div>

        {/* Title */}
        <div
          style={{
            fontSize: "0.9375rem",
            fontWeight: "600",
            marginBottom: "0.375rem",
          }}
        >
          {step.title}
        </div>

        {/* Text */}
        <div
          style={{
            fontSize: "0.875rem",
            color: "#555",
            marginBottom: "1.125rem",
          }}
        >
          {step.content}
        </div>

        {/* Standard buttons */}
        {!step.passive && !isFinal && (
          <div style={{ display: "flex", gap: "0.75rem", marginBottom: "0.875rem" }}>
            <button
              onClick={() => {
                if (step.returnToPreviousStep) {
                  goToStep("room");
                  return;
                }

                if (step.firstStep) {
                  close();                  
                }
                prev();
              }}
              style={{
                flex: 1,
                background: "#fff",
                color: "#407731",
                border: "1px solid #D8D8D8",
                borderRadius: "0.5rem",
                padding: "0.625rem",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.375rem",
              }}
            >
              <ArrowBackIcon style={{ fontSize: "1.125rem" }} />
              Précédent
            </button>
              
            <button
              onClick={() => {
                if (step.beforeOpenMenu) {
                  closeMainMenu();
                  
                }
                if (step.closeModalOnNext) {
                  dispatch(setAnchorPosition(null));
                }
                if (step.closeModalOnNext) {
                  dispatch(setOpenModal(null));
                }
                next();
              }}
                    style={{
                      flex: 1,
                      background: "#479A32",
                      color: "#fff",
                      border: "none",
                      borderRadius: "0.5",
                      padding: "0.625rem",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "0.375rem",
                      
                    }}
                  >
                  Suivant
                  <ArrowForwardIcon style={{ fontSize: "1.125rem" }} />
            </button>
          </div>
        )}

        {/* Final buttons */}
        {isFinal && (
          <div style={{ display: "flex", gap: "0.75rem", marginBottom: "0.875rem" }}>
            <button
              onClick={start}
              style={{
                flex: 1,
                background: "#fff",
                color: "#407731",
                border: "1px solid #D8D8D8",
                borderRadius: "0.5rem",
                padding: "0.625rem",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.375rem",
              }}
            >
              <span
                className="material-symbols-outlined"
                style={{ fontSize: "1.125rem" }}
              >
                replay
              </span>
              Revoir
            </button>

            <button
              onClick={close}
              style={{
                flex: 1,
                background: "#479A32",
                color: "#fff",
                border: "none",
                borderRadius: "0.5rem",
                padding: "0.625rem",
                cursor: "pointer",
              }}
            >
              Commencer
            </button>
          </div>
        )}

        {/* Dots */}
        {!step.passive && !isFinal && (
          <ProgressDots total={totalSteps} current={visibleStepIndex - 1} />
        )}

        {/* Arrow */}
        {!isFinal && !isMobile && (
          <div
            ref={arrowRef}
            style={{
              position: "absolute",
              width: step.passive ? "2.1875rem" : "3.125rem",
              height: "2.5rem",
              background: "#fff",
              transform: "rotate(45deg)",
              left: arrowData.x != null ? `${arrowData.x}px` : "",
              top: arrowData.y != null ? `${arrowData.y}px` : "",
              [arrowData.side || "bottom"]: "-0.4375rem",
              zIndex: -1,
            }}
          />
        )}
      </div>
    </>,
    document.body
  );
}